import { getLocale, t } from '../i18n';

export interface ReportPresentationSource {
  title?: string | null;
  content?: string | null;
  report_type?: string | null;
  report_data?: Record<string, unknown> | null;
}

export interface ReportFact {
  label: string;
  value: string;
}

export interface ReportSection {
  title: string;
  facts: ReportFact[];
  body?: string | null;
}

export function normalizeReportTypeKey(
  rawValue: string | null | undefined,
): string {
  const normalized = (rawValue ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  switch (normalized) {
    case 'access':
    case 'visitor':
    case 'visitors':
    case 'visitor_log':
    case 'visitor_logs':
    case 'access_log':
    case 'access_visitor_log':
      return 'access_visitor';
    case 'dar':
    case 'daily':
    case 'daily_activity':
    case 'daily_report':
    case 'daily_activity_report':
    case 'shift_summary':
    case 'shift_summary_report':
      return 'daily_activity';
    case 'patrol_report':
      return 'patrol';
    case 'use_of_force_report':
      return 'use_of_force';
    default:
      return normalized;
  }
}

export function reportTypeLabelFor(
  rawValue: string | null | undefined,
): string {
  switch (normalizeReportTypeKey(rawValue)) {
    case 'access_visitor':
      return t('Access / Visitor Log');
    case 'daily_activity':
      return t('Daily Activity Report');
    case 'incident':
      return t('Incident Report');
    case 'patrol':
      return t('Patrol Report');
    case 'maintenance':
      return t('Maintenance Report');
    case 'use_of_force':
      return t('Use of Force Report');
    case 'alarm':
      return t('Alarm Report');
    case 'general':
      return t('General Report');
    default: {
      const value = normalizeReportTypeKey(rawValue);
      if (!value) return t('Report');
      return value
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
  }
}

export function getReportTitle(report: ReportPresentationSource): string {
  const title = report.title?.trim();
  if (title) return title;

  const data = getReportData(report);
  const category = text(data.category_label) ?? text(data.category);
  if (normalizeReportTypeKey(report.report_type) === 'incident' && category) {
    return `${category} Incident`;
  }

  const content = report.content?.trim();
  if (content) {
    const words = content.split(/\s+/);
    const preview = words.slice(0, 5).join(' ');
    return words.length > 5 ? `${preview}...` : preview;
  }

  return reportTypeLabelFor(report.report_type);
}

export function getReportPreview(report: ReportPresentationSource): string {
  const data = getReportData(report);
  const type = normalizeReportTypeKey(report.report_type);

  if (type === 'incident') {
    return (
      text(data.summary) ?? text(data.what) ?? report.content?.trim() ?? ''
    );
  }

  if (type === 'access_visitor') {
    const summary = asRecord(data.summary);
    const rowCount = numberText(summary.row_count);
    if (rowCount) return `Structured access log with ${rowCount} entries.`;
  }

  if (type === 'daily_activity') {
    const summary = asRecord(data.summary);
    return (
      text(summary.text) ??
      text(data.shift_summary) ??
      text(data.summary_text) ??
      text(data.narrative) ??
      text(data.notes) ??
      report.content?.trim() ??
      ''
    );
  }

  if (type === 'patrol') {
    const summary = asRecord(data.summary);
    return (
      text(summary.text) ??
      text(data.notes) ??
      text(data.description) ??
      report.content?.trim() ??
      ''
    );
  }

  if (type === 'maintenance') {
    return text(data.description) ?? report.content?.trim() ?? '';
  }

  if (type === 'use_of_force') {
    return text(data.narrative) ?? report.content?.trim() ?? '';
  }

  return report.content?.trim() ?? '';
}

export function buildReportSections(
  report: ReportPresentationSource,
): ReportSection[] {
  const data = getReportData(report);

  switch (normalizeReportTypeKey(report.report_type)) {
    case 'incident':
      return compactSections([
        {
          title: t('Incident Details'),
          facts: compactFacts([
            [t('Incident Type'), text(data.category_label) ?? text(data.category)],
            [t('When'), formatDateTime(text(data.occurred_at))],
            [t('Where'), text(data.location) ?? text(data.where)],
            [t('People Involved'), text(data.people_involved) ?? text(data.who)],
            [t('Summary'), text(data.summary) ?? text(data.what)],
            [t('Actions Taken'), text(data.actions_taken)],
          ]),
        },
        {
          title: t('Investigation Checks'),
          facts: compactFacts([
            [t('Scene Secured'), boolLabel(data.scene_secured)],
            [t('Manager / Control Notified'), boolLabel(data.manager_notified)],
            [
              t('Emergency Services Attended'),
              boolLabel(data.emergency_services_attended),
            ],
            [t('CCTV / Access Logs Available'), boolLabel(data.cctv_available)],
            [t('Witnesses'), text(data.witnesses)],
            [t('Injuries / Medical'), text(data.injuries_or_medical)],
            [t('Damage / Loss'), text(data.damage_or_loss)],
            [t('Evidence'), text(data.evidence)],
            [t('Notifications'), text(data.notifications)],
            [t('External Reference'), text(data.external_reference)],
          ]),
          body: text(data.additional_details),
        },
      ]);
    case 'maintenance':
      return compactSections([
        {
          title: t('Maintenance Details'),
          facts: compactFacts([
            [t('Location'), text(data.location)],
            [t('Description'), text(data.description)],
            [t('Action Taken'), text(data.action_taken)],
            [t('Impact / Risk'), text(data.impact)],
            [t('Area Secured'), boolLabel(data.area_secured)],
          ]),
        },
      ]);
    case 'use_of_force':
      return compactSections([
        {
          title: t('Use of Force Details'),
          facts: compactFacts([
            [t('People Involved'), text(data.people_involved)],
            [t('Location'), text(data.location)],
            [t('When'), formatDateTime(text(data.occurred_at))],
            [t('Force Used'), text(data.force_used)],
            [t('Reason For Force'), text(data.reason_for_force)],
            [t('Medical Offered'), boolLabel(data.medical_offered)],
            [t('Police Notified'), boolLabel(data.police_notified)],
            [t('Injuries / Medical'), text(data.injuries_or_medical)],
          ]),
        },
        { title: t('Narrative'), facts: [], body: text(data.narrative) },
        { title: t('Witnesses'), facts: [], body: text(data.witnesses) },
        { title: t('Evidence'), facts: [], body: text(data.evidence) },
        {
          title: t('Notifications / References'),
          facts: [],
          body: text(data.notifications),
        },
      ]);
    case 'access_visitor':
      return buildAccessVisitorSections(data);
    case 'daily_activity':
      return buildDailyActivitySections(data);
    case 'patrol':
      return buildPatrolSections(data);
    default:
      return [];
  }
}

export function getReportData(
  report: ReportPresentationSource,
): Record<string, unknown> {
  const data = report.report_data;
  return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
}

function buildAccessVisitorSections(
  data: Record<string, unknown>,
): ReportSection[] {
  const summary = asRecord(data.summary);
  const entries = Array.isArray(data.entries)
    ? data.entries.filter(isRecord)
    : [];

  return compactSections([
    {
      title: t('Access Summary'),
      facts: compactFacts([
        [t('Rows'), numberText(summary.row_count)],
        [t('Entered'), numberText(summary.entered_count)],
        [t('Exited'), numberText(summary.exited_count)],
        [t('Visitors'), numberText(summary.visitor_count)],
        [t('Contractors'), numberText(summary.contractor_count)],
        [t('Deliveries'), numberText(summary.delivery_count)],
        [t('Staff'), numberText(summary.staff_count)],
        [t('Other'), numberText(summary.other_count)],
      ]),
    },
    ...entries.map((entry, index) => ({
      title: `Log Entry ${index + 1}`,
      facts: compactFacts([
        [t('When'), formatDateTime(text(entry.occurred_at))],
        [t('Direction'), text(entry.direction_label) ?? text(entry.direction)],
        [t('Type'), text(entry.access_type_label) ?? text(entry.access_type)],
        [t('Who'), text(entry.who)],
        [t('Organisation'), text(entry.organisation)],
        [t('Destination'), text(entry.destination)],
        [t('Details'), text(entry.details)],
      ]),
    })),
  ]);
}

function buildDailyActivitySections(
  data: Record<string, unknown>,
): ReportSection[] {
  const summary = asRecord(data.summary);
  const shift = asRecord(data.shift);
  const metrics = asRecord(data.metrics);
  const activities = arrayOfRecords(
    data.events ??
      data.activities ??
      data.activity_entries ??
      data.entries ??
      data.timeline,
  );
  const handover = asRecord(data.handover);

  return compactSections([
    {
      title: t('Shift Summary'),
      facts: compactFacts([
        [t('Date'), formatDate(text(data.shift_date) ?? text(data.date) ?? text(shift.date))],
        [t('Shift Start'), formatDateTime(text(data.shift_start_at) ?? text(data.start_at) ?? text(shift.start_at))],
        [t('Shift End'), formatDateTime(text(data.shift_end_at) ?? text(data.end_at) ?? text(shift.end_at))],
        [t('Post / Assignment'), text(data.post_name) ?? text(data.assignment) ?? text(shift.post_name)],
        [t('Check In'), formatDateTime(text(data.check_in_at) ?? text(shift.check_in_at))],
        [t('Check Out'), formatDateTime(text(data.check_out_at) ?? text(shift.check_out_at))],
        [t('Breaks'), numberText(data.break_count ?? metrics.break_count ?? summary.break_count)],
        [t('Patrols'), numberText(data.patrol_count ?? metrics.patrol_count ?? summary.patrol_count)],
        [t('Visitors'), numberText(data.visitor_count ?? metrics.visitor_count ?? summary.visitor_count)],
        [t('Incidents'), numberText(data.incident_count ?? metrics.incident_count ?? summary.incident_count)],
        [t('Events'), numberText(data.event_count ?? metrics.event_count ?? summary.event_count)],
        [t('Shift Execution'), text(data.shift_execution_id)],
      ]),
      body:
        text(data.shift_summary) ??
        text(summary.text) ??
        text(data.summary_text) ??
        text(data.narrative) ??
        text(data.notes),
    },
    {
      title: t('Handover'),
      facts: compactFacts([
        [t('Outstanding Issues'), text(data.outstanding_issues) ?? text(handover.outstanding_issues)],
        [t('Follow Up Required'), boolLabel(data.follow_up_required ?? handover.follow_up_required)],
        [t('Handover To'), text(data.handover_to) ?? text(handover.to)],
      ]),
      body: text(data.handover_notes) ?? text(handover.notes),
    },
    ...activities.map((entry, index) => ({
      title: text(entry.label) ?? `Event ${index + 1}`,
      facts: compactFacts([
        [t('When'), formatDateTime(text(entry.occurred_at) ?? text(entry.time) ?? text(entry.created_at))],
        [t('Type'), text(entry.type_label) ?? text(entry.event_type) ?? text(entry.type) ?? text(entry.category)],
        [t('Location'), text(entry.location)],
        [t('Details'), text(entry.detail) ?? text(entry.details) ?? text(entry.description) ?? text(entry.note)],
        [t('Related Report'), text(entry.report_id)],
      ]),
    })),
  ]);
}

function buildPatrolSections(data: Record<string, unknown>): ReportSection[] {
  const summary = asRecord(data.summary);
  const checkpoints = arrayOfRecords(
    data.checkpoints ?? data.checkpoint_events ?? data.entries,
  );

  return compactSections([
    {
      title: t('Patrol Summary'),
      facts: compactFacts([
        [t('Route'), text(data.patrol_name) ?? text(data.route_name) ?? text(summary.patrol_name)],
        [t('Started'), formatDateTime(text(data.started_at) ?? text(summary.started_at))],
        [t('Completed'), formatDateTime(text(data.completed_at) ?? text(summary.completed_at))],
        [t('Checkpoints'), numberText(data.checkpoint_count ?? summary.checkpoint_count)],
        [t('Completed Checkpoints'), numberText(data.completed_checkpoint_count ?? summary.completed_checkpoint_count)],
        [t('Missed Checkpoints'), numberText(data.missed_checkpoint_count ?? summary.missed_checkpoint_count)],
      ]),
      body: text(summary.text) ?? text(data.notes) ?? text(data.description),
    },
    ...checkpoints.map((checkpoint, index) => ({
      title: `Checkpoint ${index + 1}`,
      facts: compactFacts([
        [t('Name'), text(checkpoint.name) ?? text(checkpoint.checkpoint_name)],
        [t('Scanned'), formatDateTime(text(checkpoint.scanned_at) ?? text(checkpoint.completed_at))],
        [t('Status'), text(checkpoint.status)],
        [t('Notes'), text(checkpoint.notes) ?? text(checkpoint.details)],
      ]),
    })),
  ]);
}

function compactSections(sections: ReportSection[]): ReportSection[] {
  return sections.filter(
    (section) => section.facts.length > 0 || Boolean(section.body?.trim()),
  );
}

function compactFacts(
  entries: Array<[string, string | null | undefined]>,
): ReportFact[] {
  return entries
    .filter(([, value]) => Boolean(value?.trim()))
    .map(([label, value]) => ({ label, value: value?.trim() ?? '' }));
}

function text(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized ? normalized : null;
}

function boolLabel(value: unknown): string | null {
  if (typeof value === 'boolean') return value ? t('Yes') : t('No');
  const normalized = text(value)?.toLowerCase();
  if (!normalized) return null;
  if (normalized === 'true' || normalized === 'yes') return t('Yes');
  if (normalized === 'false' || normalized === 'no') return t('No');
  return null;
}

function numberText(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return text(value);
}

function formatDateTime(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(getLocale());
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(getLocale());
}

function asRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function arrayOfRecords(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
